import { useEffect, useMemo } from 'react'
import { useState } from 'react'
import { Title } from 'react-head'
import styled from 'styled-components'
import { Button, Icon, ResponsiveImage } from '@bm-js/atlascloud-ui'
import {
  breakPoints,
  breakPointsCss,
  colors,
  measurments,
} from '../utils/config'
import { fetcher } from '../utils/fetcher'
import HTMLInput from '../components/HTMLInput'
import ImageInput from '../components/ImageInput'
import Input from '../components/Input'
import Select from '../components/Select'
import Textarea from '../components/Textarea'
import DeleteConfirmation from '../components/DeleteConfirmation'
import LinkButton from '../components/LinkButton'
import Loader from '../components/Loader'
import {
  BlogPost,
  ChangeEvent,
  encodeSweUrlComponent,
  FileEntryBM,
  LucideIconName,
  parseDate,
} from '@bm-js/atlascloud-shared'
import { localDate } from '@naturalcycles/js-lib/datetime'
import type { IsoDate } from '@naturalcycles/js-lib/types'
import {
  usePopup,
  useToast,
  useToken,
  useUser,
  useWebsite,
} from '../utils/hooks'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { useTr } from '../utils/useTr'

const StyledBlogRoute = styled.div`
  label {
    display: block;
    margin-bottom: 0.5rem;
  }
  input,
  textarea {
    width: 100%;
  }
  padding-bottom: 8rem;
  .posts-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1rem;
    margin-top: 1rem;
    &.list {
      display: block;
    }
  }
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  ${breakPointsCss.tablet} {
    .headers {
      display: block;
    }
    .posts-grid {
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
    }
  }
`

const BlogRoute = () => {
  const toast = useToast()
  const token = useToken()
  const user = useUser()
  const { tr } = useTr()
  const { atlasWebsite, refetchAtlasWebsite, triggerRebuild } = useWebsite()
  const { setPopup, closePopup } = usePopup()

  const [loading, setLoading] = useState(false)
  const [searchString, setSearchString] = useState('')
  const [displayMode, setDisplayMode] = useState('list')

  const updatePost = async (post: BlogPostInput, cb: () => void) => {
    const urlTitle = encodeSweUrlComponent(post.title)
    const author = { fullName: user?.fullName }
    setLoading(true)
    const { err } = await fetcher({
      path: `customer/update-blog-post/${post._id}`,
      method: 'POST',
      body: { ...post, urlTitle, author },
      auth: token,
    })
    if (err) {
      setLoading(false)
      toast.error(tr(TrKey.blogCouldNotSavePost))
      return
    }
    cb()
    await refetchAtlasWebsite()
    await triggerRebuild()
    setLoading(false)
    toast.success(tr(TrKey.blogPostUpdated))
    openNewPostPopup(false)
  }

  const createPost = async (post: BlogPostInput, cb: () => void) => {
    const urlTitle = encodeSweUrlComponent(post.title)
    const author = { fullName: user?.fullName }
    setLoading(true)
    const { err } = await fetcher({
      method: 'POST',
      path: 'customer/create-blog-post',
      body: { ...post, urlTitle, author },
      auth: token,
    })
    if (err) {
      let message = tr(TrKey.blogCouldNotSavePost)
      if (err === 'Name already exists') {
        message = tr(TrKey.blogPostDuplicateTitle)
      }
      setLoading(false)
      toast.error(message)
      return
    }
    cb()
    await refetchAtlasWebsite()
    await triggerRebuild()
    setLoading(false)
    toast.success(tr(TrKey.blogPostSaved))
    openNewPostPopup(false)
  }

  const handleSubmit = (post: BlogPostInput, cb: () => void) => {
    if (post._id) updatePost(post, cb)
    else createPost(post, cb)
  }

  const deletePostInit = (post: BlogPost) => {
    setPopup(
      <DeleteConfirmation
        item={post}
        deleteKey={'title'}
        name={tr(TrKey.theBlogPost)}
        handleCancel={closePopup}
        handleDelete={deletePost}
      />
    )
  }

  const deletePost = async (post: BlogPost, cb: () => void) => {
    setLoading(true)
    const { err } = await fetcher({
      path: `customer/delete-blog-post/${post._id}`,
      method: 'DELETE',
      auth: token,
    })
    if (err) {
      setLoading(false)
      toast.error(tr(TrKey.blogCouldNotDeletePost))
      cb()
      return
    }
    toast.success(tr(TrKey.blogPostDeleted))
    cb()
    closePopup()
    await refetchAtlasWebsite()
    await triggerRebuild()
    setLoading(false)
  }

  const openNewPostPopup = (val: boolean, data?: BlogPost) => {
    if (val)
      setPopup(
        <BlogForm
          data={data}
          handleSubmit={handleSubmit}
          openNewPostPopup={openNewPostPopup}
        />
      )
    else closePopup()
  }

  const filteredPosts = useMemo(() => {
    //Text filtering
    const s = searchString.toLocaleLowerCase()
    let matches: BlogPost[] = []
    if (!atlasWebsite?.blogs?.[0]) return []
    if (s.length > 0) {
      atlasWebsite.blogs?.[0]?.posts?.forEach((object) => {
        if (object.title) {
          const search = object.title.toLowerCase().search(s)
          if (search !== -1) matches.push(object)
        }
      })
    } else {
      matches = atlasWebsite.blogs?.[0]?.posts
    }
    const sortedMatches = matches.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

    return sortedMatches
  }, [atlasWebsite, searchString])

  const title = useMemo(() => {
    return tr(TrKey.blogTitle) + ' | Atlascloud'
  }, [tr])

  if (!atlasWebsite || loading)
    return (
      <StyledBlogRoute>
        <div className='loader-container'>
          <Loader color={colors.primary400} />
        </div>
      </StyledBlogRoute>
    )
  if (!atlasWebsite)
    return (
      <StyledBlogRoute className='flex items-center justify-center'>
        <p
          className='mt-4'
          dangerouslySetInnerHTML={{
            __html: tr(TrKey.blogMissingWebsite),
          }}
        />
      </StyledBlogRoute>
    )
  return (
    <StyledBlogRoute className='content'>
      <Title>{title}</Title>
      <div className='headers jcsb ac flex'>
        <h1 className='m0 primblue l'>{tr(TrKey.blogTitle)}</h1>
        <Input
          noMargin
          type='text'
          placeholder={tr(TrKey.blogSearchPosts)}
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
        />
        <Select
          noMargin
          value={displayMode}
          onChange={(e) => setDisplayMode(e.target.value)}
        >
          <option value='list'>{tr(TrKey.list)}</option>
          <option value='grid'>{tr(TrKey.grid)}</option>
        </Select>
        <Button onClick={() => openNewPostPopup(true)}>
          {tr(TrKey.blogCreatePost)}
        </Button>
      </div>
      <div className={`posts-grid ${displayMode}`}>
        {filteredPosts.length ? (
          filteredPosts.map((post) => (
            <Post
              displayMode={displayMode}
              key={post._id}
              post={post}
              openNewPostPopup={openNewPostPopup}
              deletePost={() => deletePostInit(post)}
            />
          ))
        ) : (
          <p>{tr(TrKey.blogNoPostsFound)}</p>
        )}
      </div>
    </StyledBlogRoute>
  )
}

export default BlogRoute

const StyledPost = styled.div`
  border-radius: ${measurments.borderRadius}px;
  overflow: hidden;
  background: white;
  box-shadow: 0px 32px 64px -12px rgba(16, 24, 40, 0.2);
  .post-image-container {
    img {
      width: 100%;
      height: 200px;
      object-fit: cover;
    }
  }
  .post-text-container {
    padding: 1rem;
    .actions {
      margin-top: 0.5rem;
      gap: 0.5rem;
    }
  }
  &.list {
    box-shadow: none;
    background: transparent;
    .post-image-container {
      img {
        width: 50px;
        height: 50px;
        object-fit: contain;
      }
    }
    padding: 0.5rem;
    display: grid;
    grid-template-columns: 50px 1fr;
    gap: 3rem;
    position: relative;
    &:after {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 1px;
      background: ${colors.blackAlpla300};
      content: '';
    }
    .post-text-container {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      align-items: center;
      padding: 0;
      .actions {
        justify-self: flex-end;
        gap: 0.5rem;
      }
    }
  }
  ${breakPointsCss.tablet} {
    .post-image-container {
      img {
        height: 120px;
      }
    }
    .post-text-container {
      padding: 0.5rem;
    }
    &.list {
      .post-text-container {
        grid-template-columns: 1fr 1fr;
        .date {
          display: none;
        }
        .title {
          white-space: nowrap;
          overflow: hidden;
        }
      }
    }
  }
`

type PostProps = {
  post: BlogPost
  openNewPostPopup: (val: boolean, data?: BlogPost) => void
  deletePost: () => void
  displayMode: string
}

const Post = ({
  post,
  openNewPostPopup,
  deletePost,
  displayMode,
}: PostProps) => {
  const { tr } = useTr()
  const tablet = window.innerWidth < breakPoints.tablet
  return (
    <StyledPost className={`${displayMode}`}>
      <div className='post-image-container'>
        <ResponsiveImage file={post.image} />
      </div>
      <div className='post-text-container'>
        <p className='m0 title'>{post.title}</p>
        <p className='m0 date'>{parseDate(post.date)}</p>
        <div className='actions flex'>
          <Button
            onClick={() => {
              openNewPostPopup(true, post)
            }}
            withIcon={tablet}
            className='text-white'
          >
            {tablet ? (
              <Icon name={LucideIconName.Pen} className='size-5' />
            ) : (
              tr(TrKey.edit)
            )}
          </Button>
          <Button
            danger
            onClick={deletePost}
            withIcon={tablet}
            className='text-white'
          >
            {tablet ? (
              <Icon name={LucideIconName.Trash2} className='size-5' />
            ) : (
              tr(TrKey.delete)
            )}
          </Button>
        </div>
      </div>
    </StyledPost>
  )
}

type BlogPostInput = Omit<
  BlogPost,
  '_id' | 'author' | 'createdAt' | 'updatedAt' | 'urlTitle'
> & {
  _id?: string
}

type BlogFormProps = {
  data?: BlogPostInput
  handleSubmit: (post: BlogPostInput, cb: () => void) => void
  openNewPostPopup: (val: boolean) => void
}

const BlogForm = ({ data, handleSubmit, openNewPostPopup }: BlogFormProps) => {
  const initialPost: BlogPostInput = {
    title: '',
    summary: '',
    bodyHTML: '',
    date: new Date().toLocaleDateString('sv'),
    image: undefined,
    hideUntil: undefined,
  }
  const { tr } = useTr()

  const [formData, setFormData] = useState(initialPost)
  const [hasChanged, setHasChanged] = useState(true)

  const handleBlogPostChange = (
    e: ChangeEvent<string | FileEntryBM | undefined>
  ) => {
    setHasChanged(true)
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleHideUntilChange = (e: ChangeEvent<string>) => {
    setHasChanged(true)
    setFormData({
      ...formData,
      hideUntil: localDate.try(e.target.value as IsoDate)?.toISODate(),
    })
  }

  const clearHideUntil = () => {
    setHasChanged(true)
    setFormData({ ...formData, hideUntil: undefined })
  }

  const enableHideUntil = () => {
    setHasChanged(true)
    setFormData({ ...formData, hideUntil: localDate.todayString() })
  }

  useEffect(() => {
    if (data) {
      // eslint-disable-next-line
      setFormData(data)
      setHasChanged(false)
    } else setFormData(initialPost)
    // eslint-disable-next-line
  }, [data])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit(formData, () => setFormData(initialPost))
      }}
    >
      <h2 className='m0 primblue mbottom1'>
        {data ? tr(TrKey.edit) : tr(TrKey.create)} {tr(TrKey.blogPost)}
      </h2>
      <Input
        required
        label={tr(TrKey.title)}
        desc={tr(TrKey.blogPostTitleDescription)}
        type='text'
        name='title'
        id='title'
        value={formData.title}
        placeholder={tr(TrKey.blogPostTitlePlaceholder)}
        onChange={handleBlogPostChange}
      />
      <Input
        required
        label={tr(TrKey.date)}
        desc={tr(TrKey.blogPostDateDescription)}
        type='date'
        name='date'
        id='date'
        value={formData.date}
        placeholder={tr(TrKey.blogPostDatePlaceholder)}
        onChange={handleBlogPostChange}
      />
      <div style={{ marginBottom: '1.6rem' }}>
        {formData.hideUntil ? (
          <>
            <Input
              noMargin
              label={tr(TrKey.blogPostDelayPublishingUntil)}
              type='date'
              name='hideUntil'
              id='hideUntil'
              value={formData.hideUntil}
              onChange={handleHideUntilChange}
            />
            <LinkButton onClick={clearHideUntil}>
              {tr(TrKey.blogPostRemoveDelayedPublishing)}
            </LinkButton>
          </>
        ) : (
          <LinkButton onClick={enableHideUntil}>
            {tr(TrKey.blogPostDelayPublishing)}
          </LinkButton>
        )}
      </div>
      <Textarea
        required
        label={tr(TrKey.summary)}
        desc={tr(TrKey.blogPostSummaryDescription)}
        name='summary'
        id='summary'
        value={formData.summary}
        placeholder={tr(TrKey.blogPostSummaryPlaceholder)}
        onChange={handleBlogPostChange}
      />
      <ImageInput
        label={tr(TrKey.image)}
        id='image'
        desc={tr(TrKey.blogPostImageDescription)}
        onChange={(f) => {
          const e = {
            target: {
              name: 'image',
              value: f,
            },
          }
          handleBlogPostChange(e)
        }}
        file={formData.image}
      />
      <HTMLInput
        required
        label={tr(TrKey.breadText)}
        desc={tr(TrKey.blogPostBreadTextDescription)}
        id='bodyHTML'
        name='bodyHTML'
        value={formData.bodyHTML}
        onChange={handleBlogPostChange}
      />

      <div className='jce gap1 flex'>
        <Button disabled={!hasChanged} type='submit'>
          {tr(TrKey.publish)} {tr(TrKey.blogPost)}
        </Button>
        <Button danger type='button' onClick={() => openNewPostPopup(false)}>
          {tr(TrKey.cancel)}
        </Button>
      </div>
    </form>
  )
}
